import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import { colors } from '../brand-colors';

// Brand-styled button styles
const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const brandButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.red} 0%, ${colors.greyDarker} 100%)`,
};

const successButtonStyle = {
  background: `linear-gradient(180deg, ${colors.goldMid} 0%, ${colors.goldDark} 100%)`,
  border: 'none',
  color: colors.greyDarkest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const successButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.goldLight} 0%, ${colors.gold} 100%)`,
};

// IP Categories data
const IP_CATEGORIES = [
  {
    id: 'processes',
    name: 'Internal Processes or Workflows',
    shortName: 'Processes & Workflows',
    icon: '⚙️',
    description: 'Custom systems for operations, like inventory management, client onboarding, or follow-up protocols that save time or reduce errors.',
    example: 'A unique way you handle client intake, project management, or team collaboration.'
  },
  {
    id: 'cultural',
    name: 'Cultural or Experiential IP',
    shortName: 'Cultural & Experiential',
    icon: '🌍',
    description: 'Rare skills, stories, or traditions from your background that inform your work - family methods, cultural insights, or unique experiences.',
    example: 'A family recipe adapted for business, multilingual expertise, or travel-inspired philosophies.'
  },
  {
    id: 'training',
    name: 'Training or Educational Content',
    shortName: 'Training & Education',
    icon: '📚',
    description: 'Guides, scripts, or methods you\'ve created for teaching - custom sales scripts, onboarding materials, or training protocols.',
    example: 'A sales script that consistently closes, or a training method that gets new hires productive faster.'
  },
  {
    id: 'mental_models',
    name: 'Unique Mental Models or Heuristics',
    shortName: 'Mental Models',
    icon: '🧠',
    description: 'Intuitive decision-making shortcuts you\'ve developed - your "gut feel" rules that consistently work better than standard methods.',
    example: 'A hiring framework, pricing intuition, or problem-solving approach that\'s uniquely yours.'
  },
  {
    id: 'customer_insights',
    name: 'Customer Insights or Data Patterns',
    shortName: 'Customer Insights',
    icon: '📊',
    description: 'Patterns in customer behavior you\'ve noticed - informal analyses of preferences, buying triggers, or pain points.',
    example: '"Clients always need X after Y happens" - patterns you\'ve noticed that guide your recommendations.'
  },
  {
    id: 'branding',
    name: 'Branding or Marketing Nuances',
    shortName: 'Branding & Marketing',
    icon: '🎨',
    description: 'Subtle elements like signature taglines, storytelling approaches, or positioning that resonates uniquely with your audience.',
    example: 'A unique way you position services, a memorable tagline, or a storytelling approach in your marketing.'
  },
  {
    id: 'inventions',
    name: 'Unpatented Inventions or Ideas',
    shortName: 'Inventions & Ideas',
    icon: '💡',
    description: 'Novel concepts you\'ve mentally prototyped - app features, product tweaks, or workflow innovations you\'ve never documented.',
    example: 'A software tweak, product modification, or service innovation that solves a specific problem.'
  },
  {
    id: 'supplier',
    name: 'Supplier or Network Know-How',
    shortName: 'Network Know-How',
    icon: '🤝',
    description: 'Insider tips on negotiating, sourcing, or building exclusive partnerships that give you an edge.',
    example: 'Vendor negotiation tactics, sourcing secrets, or relationship-building strategies.'
  },
  {
    id: 'software',
    name: 'Software Tweaks or Digital Tools',
    shortName: 'Software & Tools',
    icon: '🔧',
    description: 'Custom scripts, spreadsheet formulas, or integrations you\'ve built to automate or improve tasks.',
    example: 'A Google Sheets macro, custom automation, or integration hack that saves significant time.'
  },
  {
    id: 'design',
    name: 'Design or Aesthetic Innovations',
    shortName: 'Design Innovations',
    icon: '✨',
    description: 'Unique layouts, packaging, or UI tweaks that make your offerings stand out visually or functionally.',
    example: 'Product packaging ideas, interface improvements, or visual design approaches.'
  }
];

// Discovery mode category order
const DISCOVERY_ORDER = [
  'processes',
  'cultural',
  'training',
  'mental_models',
  'customer_insights',
  'other' // Catch-all for remaining categories
];

// Warm-up questions (used for both paths)
const WARMUP_QUESTIONS = [
  "What's the one thing your clients or customers always compliment you on?",
  "If you had to teach a new employee 'the secret sauce' of how things really work here, what would you focus on first?",
  "What part of your business do you wish your competitors could never copy?"
];

// Category-specific questions
const CATEGORY_QUESTIONS = {
  processes: [
    "Walk me through your most important custom workflow - the one that really sets you apart. What happens step by step?",
    "How did you develop this process? Was it trial and error, a past experience, or something else?",
    "How much time or money does this save compared to the standard way of doing things?",
    "If a competitor learned your exact workflow, would it hurt your business?"
  ],
  cultural: [
    "What unique background, experiences, or traditions influence how you approach your work?",
    "Have you created content like guides, videos, or frameworks based on your unique perspective?",
    "How does this cultural or experiential knowledge give you an edge others don't have?",
    "Could you teach this perspective to others, or is it something deeply personal?"
  ],
  training: [
    "What training materials, scripts, or methods have you created that consistently get results?",
    "Walk me through how you onboard or train someone on your approach. What's the key thing you teach them?",
    "What would happen if a competitor got their hands on your training materials?",
    "Could this training content be packaged and sold as a product or course?"
  ],
  mental_models: [
    "Describe your 'gut feel' rule or shortcut for making key decisions. How does it work?",
    "Have you trained someone on this 'your way' of thinking? How did that go?",
    "When facing a common industry challenge, how does your approach differ from what most people do?",
    "If you had to name this decision-making framework, what would you call it?"
  ],
  customer_insights: [
    "What patterns have you noticed in customer behavior that guide your strategies?",
    "Tell me about a time you used one of these patterns to predict what a client needed.",
    "Do you track or mentally note data that's not publicly available in your industry?",
    "Could these insights be turned into a predictive model or scoring system?"
  ],
  branding: [
    "What unique branding elements do you have - taglines, design approaches, or storytelling methods?",
    "How did you develop your positioning? What makes it resonate with your audience?",
    "What would someone lose if they tried to copy your brand without understanding the thinking behind it?",
    "Could any of these branding elements be trademarked or licensed?"
  ],
  inventions: [
    "Describe the novel idea or modification you've developed but never formally documented.",
    "What specific problem does this invention or idea solve?",
    "How did you come up with this? What triggered the innovation?",
    "Could this be turned into a product, service, or patentable method?"
  ],
  supplier: [
    "What insider knowledge do you have about working with vendors, suppliers, or partners?",
    "How did you develop these relationships or learn these negotiation tactics?",
    "What advantage does this give you over competitors?",
    "Could you teach someone else to replicate these relationships?"
  ],
  software: [
    "Describe the custom script, formula, or automation you've created.",
    "How much time does this save you compared to doing it manually?",
    "Could this tool be turned into a product others would pay for?",
    "What's the core algorithm or logic that makes it work?"
  ],
  design: [
    "What unique design or aesthetic elements have you created?",
    "How does this design approach improve the user or customer experience?",
    "What's the thinking behind the design that others might not immediately understand?",
    "Could this be protected as a design patent or used as a differentiator?"
  ]
};

// Follow-up prompts for deeper exploration
const FOLLOWUP_PROMPTS = {
  processes: [
    "Walk me through that more specifically - what happens first, second, etc?",
    "How did you come up with this approach? Was it trial and error, or a past experience?",
    "What's the secret that makes this work that most people would miss?"
  ],
  default: [
    "Can you give me a specific example of when this happened?",
    "What would you call this approach if you had to give it a name?",
    "Why do you think this works when other approaches don't?"
  ]
};

/**
 * MindToMethodV2 - Category-First IP Extraction
 * Alternative approach to IP extraction with upfront category selection
 */
function MindToMethodV2() {
  // Core state
  const [phase, setPhase] = useState('capture'); // capture, category_selection, interview, output
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingSession, setExistingSession] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: ''
  });

  // Category selection state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [currentDiscoveryIndex, setCurrentDiscoveryIndex] = useState(0);

  // Interview state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [aiResponse, setAiResponse] = useState('');
  const [warmupComplete, setWarmupComplete] = useState(false);

  // Output state
  const [output, setOutput] = useState(null);
  const [pdfSending, setPdfSending] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [updatedEmail, setUpdatedEmail] = useState('');

  const answerInputRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Auto-scroll conversation
  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Check if email exists
  const checkEmail = async () => {
    if (!formData.email) return;

    try {
      const response = await fetch('/api/mind-to-method-v2/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.toLowerCase() })
      });
      const data = await response.json();

      if (data.exists) {
        setExistingSession(data);
      } else {
        setExistingSession(null);
      }
    } catch (err) {
      // Ignore check errors
    }
  };

  // Start session
  const handleStart = async () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/mind-to-method-v2/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          company: formData.company.trim(),
          industry: formData.industry.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setSessionId(data.session_id);

        if (data.is_existing && data.output_generated) {
          // Load existing output
          setPhase('output');
          loadExistingOutput(data.session_id);
        } else {
          // New session - go to category selection
          setPhase('category_selection');
        }
      } else {
        setError(data.error || 'Failed to start session');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Load existing output
  const loadExistingOutput = async (id) => {
    try {
      const response = await fetch(`/api/mind-to-method-v2/get-output/${id}`);
      const data = await response.json();
      if (data.success) {
        setOutput(data.output);
      }
    } catch (err) {
      setError('Failed to load previous results');
    }
  };

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setDiscoveryMode(false);
  };

  // Handle "I don't know" selection
  const handleDiscoveryMode = () => {
    setSelectedCategory(null);
    setDiscoveryMode(true);
  };

  // Start interview with selected approach
  const startInterview = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method-v2/start-interview/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: selectedCategory,
          discovery_mode: discoveryMode
        })
      });

      const data = await response.json();

      if (data.success) {
        // Build questions list based on mode
        let questionList = [];

        // Always start with 2 warmup questions
        questionList.push({
          type: 'warmup',
          question: WARMUP_QUESTIONS[0],
          key: 'warmup_1'
        });
        questionList.push({
          type: 'warmup',
          question: WARMUP_QUESTIONS[1],
          key: 'warmup_2'
        });

        if (discoveryMode) {
          // Discovery mode - add questions from first priority category
          const firstCategory = DISCOVERY_ORDER[0];
          const categoryQuestions = CATEGORY_QUESTIONS[firstCategory] || [];
          categoryQuestions.slice(0, 3).forEach((q, idx) => {
            questionList.push({
              type: 'category',
              category: firstCategory,
              question: q,
              key: `${firstCategory}_${idx}`
            });
          });
        } else if (selectedCategory && CATEGORY_QUESTIONS[selectedCategory]) {
          // Selected category - add all category questions
          CATEGORY_QUESTIONS[selectedCategory].forEach((q, idx) => {
            questionList.push({
              type: 'category',
              category: selectedCategory,
              question: q,
              key: `${selectedCategory}_${idx}`
            });
          });
        }

        setQuestions(questionList);
        setCurrentQuestionIndex(0);
        setConversationHistory([{
          role: 'assistant',
          content: `Great, ${formData.name}! Let's discover your hidden intellectual property. I'll ask you a few questions to help extract what makes you unique.\n\n${questionList[0].question}`
        }]);
        setPhase('interview');
      } else {
        setError(data.error || 'Failed to start interview');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Submit answer
  const handleSubmitAnswer = async () => {
    if (!currentAnswer.trim()) {
      setError('Please provide an answer');
      return;
    }

    setLoading(true);
    setError('');

    const currentQ = questions[currentQuestionIndex];

    // Add answer to history
    const newHistory = [...conversationHistory, {
      role: 'user',
      content: currentAnswer
    }];

    // Save answer
    const newAnswers = {
      ...answers,
      [currentQ.key]: currentAnswer
    };
    setAnswers(newAnswers);

    try {
      // Send answer to backend for processing
      const response = await fetch(`/api/mind-to-method-v2/answer/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_key: currentQ.key,
          question_type: currentQ.type,
          category: currentQ.category || selectedCategory || (discoveryMode ? DISCOVERY_ORDER[currentDiscoveryIndex] : null),
          answer: currentAnswer,
          all_answers: newAnswers
        })
      });

      const data = await response.json();

      if (data.success) {
        // Check if we need to move to next category in discovery mode
        if (discoveryMode && data.switch_category && currentDiscoveryIndex < DISCOVERY_ORDER.length - 1) {
          const nextCategoryIndex = currentDiscoveryIndex + 1;
          const nextCategory = DISCOVERY_ORDER[nextCategoryIndex];
          setCurrentDiscoveryIndex(nextCategoryIndex);

          // Add questions from next category
          const nextCategoryQuestions = CATEGORY_QUESTIONS[nextCategory] || [];
          const newQuestions = nextCategoryQuestions.slice(0, 3).map((q, idx) => ({
            type: 'category',
            category: nextCategory,
            question: q,
            key: `${nextCategory}_${idx}`
          }));

          setQuestions([...questions, ...newQuestions]);
        }

        // Move to next question or finish
        if (currentQuestionIndex + 1 < questions.length && !data.completed) {
          const nextQ = questions[currentQuestionIndex + 1];

          // Generate AI transition response
          const aiTransition = data.ai_response || `Good insight. ${nextQ.question}`;

          newHistory.push({
            role: 'assistant',
            content: aiTransition
          });

          setConversationHistory(newHistory);
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setCurrentAnswer('');
        } else {
          // Interview complete - generate output
          newHistory.push({
            role: 'assistant',
            content: "Excellent! I've gathered enough information. Let me analyze what you've shared and extract your key IP assets..."
          });
          setConversationHistory(newHistory);
          await generateOutput();
        }
      } else {
        setError(data.error || 'Failed to process answer');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
    setTimeout(() => answerInputRef.current?.focus(), 100);
  };

  // Generate output
  const generateOutput = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method-v2/generate-output/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setOutput(data.output);
        setPhase('output');
      } else {
        setError(data.error || 'Failed to generate IP summary');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Send PDF via email
  const handleSendPdf = async (useUpdatedEmail = false) => {
    setPdfSending(true);
    setEmailError('');

    const emailToUse = useUpdatedEmail && updatedEmail ? updatedEmail.trim().toLowerCase() : null;

    try {
      const response = await fetch(`/api/mind-to-method-v2/send-pdf/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailToUse ? { email: emailToUse } : {})
      });

      const data = await response.json();

      if (data.success) {
        setPdfSent(true);
        setEmailError('');
      } else {
        setEmailError(data.error || 'Failed to send PDF.');
        setUpdatedEmail(formData.email);
      }
    } catch (err) {
      setEmailError('Failed to send PDF. Please try again.');
      setUpdatedEmail(formData.email);
    }

    setPdfSending(false);
  };

  // Celebrate with confetti on output
  useEffect(() => {
    if (phase === 'output' && output && !loading) {
      const end = Date.now() + 1500;
      const confettiColors = ['#a10000', '#ba1d1d', '#db3838', '#ddb75e', '#cca557', '#ba934f'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: confettiColors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: confettiColors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [phase, output, loading]);

  // Calculate progress
  const getProgress = () => {
    if (phase === 'capture') return 0;
    if (phase === 'category_selection') return 15;
    if (phase === 'interview') {
      const base = 20;
      const interviewProgress = questions.length > 0
        ? ((currentQuestionIndex + 1) / questions.length) * 60
        : 0;
      return Math.round(base + interviewProgress);
    }
    return 100;
  };

  // ============================================
  // RENDER: Capture Phase
  // ============================================
  if (phase === 'capture') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h1 className="text-center mb-2" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a365d' }}>
                  Mind to Method
                </h1>
                <p className="text-center text-muted mb-4">
                  Discover your hidden intellectual property in 10 minutes
                </p>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {existingSession && (
                  <Alert variant="info">
                    We found an existing session for this email.
                    {existingSession.output_generated
                      ? " Click below to view your results."
                      : " Click below to continue where you left off."}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label><strong>Your Name</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Sarah Johnson"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Email Address</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={checkEmail}
                  />
                  <Form.Text className="text-muted">We'll send your IP report here.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Company Name</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Acme Consulting"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Industry/Expertise Area</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Marketing Strategy, Legal Services"
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                  />
                </Form.Group>

                <Button
                  size="lg"
                  className="w-100"
                  onClick={handleStart}
                  disabled={loading}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Starting...</>
                  ) : existingSession ? (
                    existingSession.output_generated ? 'View My Results' : 'Continue My Session'
                  ) : (
                    'Start IP Extraction'
                  )}
                </Button>

                <p className="text-center text-muted mt-4" style={{ fontSize: '0.85rem' }}>
                  Takes about 10 minutes. Your progress is saved automatically.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Category Selection Phase
  // ============================================
  if (phase === 'category_selection') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="text-center mb-3" style={{ color: '#1a365d' }}>
                  What Type of IP Might You Have?
                </h2>
                <p className="text-center text-muted mb-4">
                  Select the category that best describes your hidden expertise, or let us help you discover it.
                </p>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                <Row>
                  {IP_CATEGORIES.map((cat) => (
                    <Col md={6} key={cat.id} className="mb-3">
                      <Card
                        className={`h-100 cursor-pointer ${selectedCategory === cat.id ? 'border-primary' : ''}`}
                        style={{
                          cursor: 'pointer',
                          borderWidth: selectedCategory === cat.id ? '2px' : '1px',
                          backgroundColor: selectedCategory === cat.id ? '#f0f7ff' : '#fff',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleCategorySelect(cat.id)}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-start">
                            <span className="me-2" style={{ fontSize: '1.5rem' }}>{cat.icon}</span>
                            <div>
                              <h6 className="mb-1" style={{ color: selectedCategory === cat.id ? '#0d6efd' : '#1a365d' }}>
                                {cat.shortName}
                              </h6>
                              <p className="mb-0 small text-muted">{cat.description}</p>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* I don't know option */}
                <Card
                  className={`mt-3 ${discoveryMode ? 'border-warning' : ''}`}
                  style={{
                    cursor: 'pointer',
                    borderWidth: discoveryMode ? '2px' : '1px',
                    backgroundColor: discoveryMode ? '#fffbeb' : '#fff',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={handleDiscoveryMode}
                >
                  <Card.Body className="p-3 text-center">
                    <h6 className="mb-1" style={{ color: discoveryMode ? '#d97706' : '#1a365d' }}>
                      🔍 I'm Not Sure What I Have
                    </h6>
                    <p className="mb-0 small text-muted">
                      That's okay! We'll ask some discovery questions to help identify your hidden IP.
                    </p>
                  </Card.Body>
                </Card>

                <Button
                  size="lg"
                  className="w-100 mt-4"
                  onClick={startInterview}
                  disabled={loading || (!selectedCategory && !discoveryMode)}
                  style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Starting...</>
                  ) : (
                    <>Continue {discoveryMode ? 'with Discovery' : `with ${IP_CATEGORIES.find(c => c.id === selectedCategory)?.shortName || ''}`} →</>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Interview Phase
  // ============================================
  if (phase === 'interview') {
    const currentQ = questions[currentQuestionIndex];
    const isWarmup = currentQ?.type === 'warmup';

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                {/* Progress */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge style={{ backgroundColor: colors.redDark }}>
                      {isWarmup ? 'Warm-up' : (discoveryMode ? `Discovery: ${IP_CATEGORIES.find(c => c.id === currentQ?.category)?.shortName || 'Exploring'}` : IP_CATEGORIES.find(c => c.id === selectedCategory)?.shortName)}
                    </Badge>
                    <span className="text-muted small">{getProgress()}% complete</span>
                  </div>
                  <ProgressBar now={getProgress()} style={{ height: '8px', backgroundColor: colors.greyLight }} className="brand-progress" />
                  <p className="mt-2 mb-0 small text-muted">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </p>
                </div>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {/* Conversation History */}
                <div
                  className="mb-4 p-3"
                  style={{
                    maxHeight: '350px',
                    overflowY: 'auto',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {conversationHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`mb-3 p-3 rounded ${
                        msg.role === 'assistant'
                          ? 'bg-white border'
                          : 'bg-primary text-white ms-4'
                      }`}
                      style={{
                        maxWidth: msg.role === 'user' ? '85%' : '100%',
                        marginLeft: msg.role === 'user' ? 'auto' : '0'
                      }}
                    >
                      {msg.role === 'assistant' && (
                        <small className="text-muted d-block mb-1">AI Coach</small>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    </div>
                  ))}
                  <div ref={conversationEndRef} />
                </div>

                {/* Answer Input */}
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Type your answer here... Be specific - concrete examples help us extract better IP."
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    ref={answerInputRef}
                    disabled={loading}
                  />
                </Form.Group>

                <Button
                  size="lg"
                  className="w-100"
                  onClick={handleSubmitAnswer}
                  disabled={loading || !currentAnswer.trim()}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Processing...</>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Output Phase
  // ============================================
  if (phase === 'output') {
    if (loading || !output) {
      return (
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p>Analyzing your responses and extracting IP assets...</p>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#1a365d' }}>Your IP Extraction Complete!</h2>
              <p className="mb-0">Here's the intellectual property we discovered.</p>
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {/* IP Assets */}
            {output.ip_assets && output.ip_assets.length > 0 && (
              <Card className="shadow mb-4">
                <Card.Header className="bg-primary text-white py-3">
                  <h4 className="mb-0">Discovered IP Assets</h4>
                </Card.Header>
                <Card.Body className="p-4">
                  {output.ip_assets.map((asset, idx) => (
                    <div key={idx} className={idx > 0 ? 'mt-4 pt-4 border-top' : ''}>
                      <div className="d-flex align-items-start mb-2">
                        <Badge
                          className="me-2"
                          style={{
                            backgroundColor: asset.value === 'high' ? '#2f855a' : asset.value === 'medium' ? '#d69e2e' : '#718096'
                          }}
                        >
                          {asset.value === 'high' ? 'High Value' : asset.value === 'medium' ? 'Medium Value' : 'Potential'}
                        </Badge>
                        <Badge style={{ backgroundColor: colors.greyMid }}>
                          {IP_CATEGORIES.find(c => c.id === asset.category)?.shortName || asset.category}
                        </Badge>
                      </div>
                      <h5 className="mb-2">{asset.name}</h5>
                      <p className="text-muted mb-2">{asset.description}</p>
                      {asset.next_steps && (
                        <div className="small" style={{ color: '#2f855a' }}>
                          <strong>Next Steps:</strong> {asset.next_steps}
                        </div>
                      )}
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            {/* Summary */}
            {output.summary && (
              <Card className="shadow mb-4">
                <Card.Header style={{ backgroundColor: colors.greyLightest }}>
                  <h5 className="mb-0">Summary</h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{output.summary}</p>
                </Card.Body>
              </Card>
            )}

            {/* Recommendations */}
            {output.recommendations && output.recommendations.length > 0 && (
              <Card className="shadow mb-4" style={{ borderColor: '#2f855a' }}>
                <Card.Header style={{ backgroundColor: '#f0fff4', borderColor: '#2f855a' }}>
                  <h5 className="mb-0" style={{ color: '#2f855a' }}>Recommended Actions</h5>
                </Card.Header>
                <Card.Body>
                  <ul className="mb-0">
                    {output.recommendations.map((rec, idx) => (
                      <li key={idx} className="mb-2">{rec}</li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            )}

            {/* Email PDF Section */}
            <Card className="mb-4" style={{ backgroundColor: '#f8fafc' }}>
              <Card.Body className="p-4 text-center">
                <h5 className="mb-3">Get Your IP Report as a PDF</h5>
                <p className="text-muted mb-3">
                  We'll email your complete IP extraction report to preserve your work.
                </p>

                {!pdfSent && !emailError && (
                  <Button
                    size="lg"
                    onClick={() => handleSendPdf(false)}
                    disabled={pdfSending}
                    style={brandButtonStyle}
                    onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                    onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                  >
                    {pdfSending ? (
                      <><Spinner size="sm" className="me-2" />Sending to {formData.email}...</>
                    ) : (
                      <>Email PDF to {formData.email}</>
                    )}
                  </Button>
                )}

                {emailError && !pdfSent && (
                  <div className="mt-3">
                    <Alert variant="warning">{emailError}</Alert>
                    <p className="text-muted mb-2">Update your email address and try again:</p>
                    <div className="d-flex gap-2 justify-content-center">
                      <Form.Control
                        type="email"
                        placeholder="your@email.com"
                        value={updatedEmail}
                        onChange={(e) => setUpdatedEmail(e.target.value)}
                        style={{ maxWidth: '300px' }}
                      />
                      <Button
                        onClick={() => handleSendPdf(true)}
                        disabled={pdfSending || !updatedEmail.trim()}
                        style={{...brandButtonStyle, padding: '0.5rem 1rem', fontSize: '1rem'}}
                      >
                        {pdfSending ? <Spinner size="sm" /> : 'Retry'}
                      </Button>
                    </div>
                  </div>
                )}

                {pdfSent && (
                  <Alert variant="success" className="mb-0">
                    PDF sent to {formData.email}! Check your inbox.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Next Steps CTA */}
            <Card className="shadow">
              <Card.Body className="p-4">
                <h4 className="mb-3">You Have More IP Than You Realize</h4>
                <p className="text-muted mb-3">
                  Most experts have <strong>5-10 pieces</strong> of valuable intellectual property
                  they've never extracted. Inside the <strong>Command Center</strong>, we help you
                  extract ALL your valuable IP and turn it into marketing that works.
                </p>
                <Button
                  size="lg"
                  href="https://www.marketdomination.solutions/ai-profit-engineer/"
                  target="_blank"
                  className="w-100"
                  style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                >
                  Explore The Command Center
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default MindToMethodV2;
